import { useState, useRef } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const serviceOptions = [
  'Vehicle Export (EV & Automobiles)',
  'Construction Machinery Supply',
  'Project Procurement & Sourcing',
  'International Trade & Logistics',
  'Multiple Services',
];

const marketOptions = [
  'Ethiopia',
  'Djibouti',
  'Dubai / UAE',
  'Other African Market',
  'Other Middle East Market',
];

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function Contact() {
  const { ref, isVisible } = useScrollAnimation();
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation();
  const formElement = useRef<HTMLFormElement>(null);

  const [charCount, setCharCount] = useState(0);
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = formElement.current;
    if (!form) return;

    const details = (form.elements.namedItem('project_details') as HTMLTextAreaElement)?.value || '';
    if (details.length > 500) {
      setErrorMsg('Project details must be 500 characters or fewer.');
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    const data = new URLSearchParams();
    const fields = form.elements;
    for (let i = 0; i < fields.length; i++) {
      const el = fields[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (el.name && el.tagName !== 'BUTTON') {
        data.append(el.name, el.value);
      }
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d71vsgmuimmp87kg1q0g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
        setErrorMsg('Submission failed. Please try again or email us directly.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please try again or email us directly.');
    }
  };

  return (
    <section id="contact" className="py-32 bg-charcoal relative overflow-hidden">
      {/* Background subtle texture */}
      <div className="absolute inset-0 opacity-[0.04]">
        <img
          src="https://public.readdy.ai/ai/img_res/db1576bace1540417a3993a8d426d56b.jpg"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">

        {/* Section Header */}
        <div
          ref={ref}
          className={`mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="text-gold text-[10px] font-semibold tracking-[0.35em] uppercase mb-5 block">
            Contact &amp; Inquiries
          </span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-heading text-4xl md:text-5xl font-light text-white leading-tight">
              Let&apos;s Discuss<br />
              <em className="not-italic text-gold">Your Project</em>
            </h2>
            <p className="text-[#7A7A7A] text-sm leading-relaxed tracking-wide max-w-md">
              Whether sourcing vehicles, procuring for a large development, or planning a machinery import — reach out directly. We respond promptly and take every inquiry seriously.
            </p>
          </div>
          <div className="section-divider mt-8" />
        </div>

        {/* Two-column: Contact Card + Form */}
        <div
          ref={formRef}
          className={`grid grid-cols-1 lg:grid-cols-5 gap-10 transition-all duration-1000 delay-200 ${
            formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* LEFT: Contact Card (2/5) */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            {/* Executive Card */}
            <div className="border border-charcoal-border p-8 relative">
              <div className="absolute top-0 right-0 w-10 h-10 border-t border-r border-gold" />
              <div className="absolute bottom-0 left-0 w-10 h-10 border-b border-l border-gold" />

              <div className="mb-6 pb-6 border-b border-charcoal-border">
                <div className="font-heading text-2xl text-white font-light">Sabir Bedru Teki</div>
                <div className="text-[10px] text-gold tracking-[0.25em] uppercase font-medium mt-1">
                  CEO &amp; Founder
                </div>
                <div className="text-[11px] text-charcoal-text tracking-widest mt-0.5">
                  ABUMI Trading Company Limited
                </div>
              </div>

              <div className="flex flex-col gap-5">
                <a href="tel:+8613690236065" className="flex items-center gap-4 group cursor-pointer">
                  <div className="w-10 h-10 flex items-center justify-center border border-charcoal-border group-hover:border-gold/50 transition-colors duration-300 flex-shrink-0">
                    <i className="ri-phone-line text-gold" />
                  </div>
                  <div>
                    <div className="text-[9px] text-charcoal-text tracking-[0.2em] uppercase mb-0.5">China Office</div>
                    <div className="text-[#C0C0C0] text-sm font-light tracking-wider group-hover:text-gold transition-colors duration-300">
                      +86 136 9023 6065
                    </div>
                  </div>
                </a>

                <a href="tel:+251915730165" className="flex items-center gap-4 group cursor-pointer">
                  <div className="w-10 h-10 flex items-center justify-center border border-charcoal-border group-hover:border-gold/50 transition-colors duration-300 flex-shrink-0">
                    <i className="ri-phone-line text-gold" />
                  </div>
                  <div>
                    <div className="text-[9px] text-charcoal-text tracking-[0.2em] uppercase mb-0.5">Ethiopia Office</div>
                    <div className="text-[#C0C0C0] text-sm font-light tracking-wider group-hover:text-gold transition-colors duration-300">
                      +251 915 730 165
                    </div>
                  </div>
                </a>

                <a href="mailto:info@abumitrading.com" className="flex items-center gap-4 group cursor-pointer">
                  <div className="w-10 h-10 flex items-center justify-center border border-charcoal-border group-hover:border-gold/50 transition-colors duration-300 flex-shrink-0">
                    <i className="ri-mail-line text-gold" />
                  </div>
                  <div>
                    <div className="text-[9px] text-charcoal-text tracking-[0.2em] uppercase mb-0.5">Email</div>
                    <div className="text-[#C0C0C0] text-sm font-light tracking-wider group-hover:text-gold transition-colors duration-300">
                      info@abumitrading.com
                    </div>
                  </div>
                </a>

                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center border border-charcoal-border flex-shrink-0">
                    <i className="ri-map-pin-2-line text-gold" />
                  </div>
                  <div>
                    <div className="text-[9px] text-charcoal-text tracking-[0.2em] uppercase mb-0.5">Registration</div>
                    <div className="text-[#C0C0C0] text-sm font-light tracking-wider">
                      Hong Kong, China
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quote */}
            <blockquote className="border-l-2 border-gold pl-6 py-2">
              <p className="font-heading text-lg text-[#C0C0C0] font-light italic leading-relaxed">
                &ldquo;Large-scale deals require trusted partners. We are built for exactly that.&rdquo;
              </p>
            </blockquote>
          </div>

          {/* RIGHT: Quote Request Form (3/5) */}
          <div className="lg:col-span-3">
            <div className="border border-charcoal-border p-8 md:p-10 relative">
              <div className="absolute top-0 right-0 w-10 h-10 border-t border-r border-gold" />
              <div className="absolute bottom-0 left-0 w-10 h-10 border-b border-l border-gold" />

              <div className="mb-8">
                <div className="text-[10px] text-gold tracking-[0.3em] uppercase font-semibold mb-2">Request a Quote</div>
                <p className="text-[#7A7A7A] text-xs leading-relaxed">
                  Fill in the details below and we will respond with a tailored solution within 24 hours.
                </p>
              </div>

              {/* Success State */}
              {status === 'success' ? (
                <div className="py-16 flex flex-col items-center justify-center text-center gap-5">
                  <div className="w-16 h-16 rounded-full border border-gold/40 flex items-center justify-center">
                    <i className="ri-check-line text-gold text-2xl" />
                  </div>
                  <div>
                    <div className="font-heading text-xl text-white font-light mb-2">Inquiry Received</div>
                    <p className="text-[#7A7A7A] text-sm leading-relaxed max-w-xs">
                      Thank you. Our team will review your request and respond within 24 hours.
                    </p>
                  </div>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-2 px-6 py-2.5 border border-gold/50 text-gold text-[10px] tracking-[0.2em] uppercase hover:border-gold hover:bg-gold/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    Submit Another Inquiry
                  </button>
                </div>
              ) : (
                <form
                  ref={formElement}
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  {/* Row 1: Name + Company */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[9px] text-charcoal-text tracking-[0.2em] uppercase">
                        Full Name <span className="text-gold">*</span>
                      </label>
                      <input
                        type="text"
                        name="full_name"
                        required
                        placeholder="Your full name"
                        className="bg-transparent border border-charcoal-border focus:border-gold/60 outline-none px-4 py-3 text-[#C0C0C0] text-sm placeholder-[#444] tracking-wide transition-colors duration-300"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[9px] text-charcoal-text tracking-[0.2em] uppercase">
                        Company Name
                      </label>
                      <input
                        type="text"
                        name="company_name"
                        placeholder="Your company (if applicable)"
                        className="bg-transparent border border-charcoal-border focus:border-gold/60 outline-none px-4 py-3 text-[#C0C0C0] text-sm placeholder-[#444] tracking-wide transition-colors duration-300"
                      />
                    </div>
                  </div>

                  {/* Row 2: Email + Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[9px] text-charcoal-text tracking-[0.2em] uppercase">
                        Email Address <span className="text-gold">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="your@email.com"
                        className="bg-transparent border border-charcoal-border focus:border-gold/60 outline-none px-4 py-3 text-[#C0C0C0] text-sm placeholder-[#444] tracking-wide transition-colors duration-300"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[9px] text-charcoal-text tracking-[0.2em] uppercase">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="+1 000 000 0000"
                        className="bg-transparent border border-charcoal-border focus:border-gold/60 outline-none px-4 py-3 text-[#C0C0C0] text-sm placeholder-[#444] tracking-wide transition-colors duration-300"
                      />
                    </div>
                  </div>

                  {/* Row 3: Service + Market */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[9px] text-charcoal-text tracking-[0.2em] uppercase">
                        Service Interest <span className="text-gold">*</span>
                      </label>
                      <div className="relative">
                        <select
                          name="service_interest"
                          required
                          defaultValue=""
                          className="w-full bg-charcoal border border-charcoal-border focus:border-gold/60 outline-none px-4 py-3 text-sm text-[#C0C0C0] tracking-wide appearance-none cursor-pointer transition-colors duration-300"
                        >
                          <option value="" disabled className="text-[#444]">Select a service</option>
                          {serviceOptions.map((opt) => (
                            <option key={opt} value={opt} className="bg-[#1A1A1A] text-[#C0C0C0]">{opt}</option>
                          ))}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-down-s-line text-gold text-sm" />
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[9px] text-charcoal-text tracking-[0.2em] uppercase">
                        Destination Market
                      </label>
                      <div className="relative">
                        <select
                          name="destination_market"
                          defaultValue=""
                          className="w-full bg-charcoal border border-charcoal-border focus:border-gold/60 outline-none px-4 py-3 text-sm text-[#C0C0C0] tracking-wide appearance-none cursor-pointer transition-colors duration-300"
                        >
                          <option value="" className="text-[#444]">Select market</option>
                          {marketOptions.map((opt) => (
                            <option key={opt} value={opt} className="bg-[#1A1A1A] text-[#C0C0C0]">{opt}</option>
                          ))}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-down-s-line text-gold text-sm" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-[9px] text-charcoal-text tracking-[0.2em] uppercase">
                        Project Details <span className="text-gold">*</span>
                      </label>
                      <span className={`text-[9px] tracking-wide ${charCount > 450 ? 'text-gold' : 'text-[#444]'}`}>
                        {charCount}/500
                      </span>
                    </div>
                    <textarea
                      name="project_details"
                      required
                      rows={4}
                      maxLength={500}
                      placeholder="Describe your project — quantity, specifications, timeline, destination, and any other relevant details..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="bg-transparent border border-charcoal-border focus:border-gold/60 outline-none px-4 py-3 text-[#C0C0C0] text-sm placeholder-[#444] tracking-wide resize-none transition-colors duration-300 leading-relaxed"
                    />
                  </div>

                  {/* Error message */}
                  {errorMsg && (
                    <p className="text-[11px] text-red-400/80 tracking-wide">{errorMsg}</p>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="mt-2 w-full py-4 bg-gold text-charcoal text-xs font-semibold tracking-[0.18em] uppercase hover:bg-gold-light transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                  >
                    {status === 'submitting' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-sm" />
                        Sending Inquiry...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line text-sm" />
                        Submit Quote Request
                      </>
                    )}
                  </button>

                  <p className="text-[9px] text-[#444] tracking-wide text-center">
                    We respect your privacy. Your information is never shared with third parties.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
